<template>
  <div>
    <div class="purchase-fish-content">
      <div class="purchase-fish-header">
        <router-link to="/mypage" class="back-link">
          <chevron-left-icon :size="16" fillColor="#67A9E7" />
          {{ t("purchaseFish.backToMyPage") }}
        </router-link>
        <div class="purchase-fish-header-title">{{ t("purchaseFish.title") }}</div>
      </div>
      <!-- Fish残高情報 -->
      <div v-if="fishError" class="fish-info fish-error">
        <p class="error-message">{{ fishError }}</p>
        <button class="retry-button" @click="retryFetchFishData">{{ t("purchaseFish.fishBalance.errorRetry") }}</button>
      </div>
      <div v-else class="fish-info">
        <div class="fish-info-title">{{ t("purchaseFish.fishBalance.title") }}</div>
        <div class="fish-info-data">
          <div class="fish-info-data-title">
            <img
              class="fish-info-data-title-icon"
              src="@/assets/img/icon/fish_icon.png"
              :alt="t('purchaseFish.product.fishIcon')"
            />
            <span v-if="fishLoading" class="skeleton-text skeleton-amount"></span>
            <template v-else>{{ formattedCurrentFish }}</template>
          </div>
          <div class="fish-info-data-description">
            <div class="fish-info-data-description-item">
              <div class="fish-category">{{ t("purchaseFish.fishBalance.charge") }}</div>
              <div class="fish-value">
                <span v-if="fishLoading" class="skeleton-text skeleton-value"></span>
                <template v-else>{{ formattedPrepaidFish }}</template>
              </div>
            </div>
            <div class="fish-info-data-description-item-plus"><PlusIcon></PlusIcon></div>
            <div class="fish-info-data-description-item">
              <div class="fish-category">{{ t("purchaseFish.fishBalance.bonus") }}</div>
              <div class="fish-value">
                <span v-if="fishLoading" class="skeleton-text skeleton-value"></span>
                <template v-else>{{ formattedFreeFish }}</template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- プレミアム誘導バナー -->
      <div v-if="isFreePlan" class="premium-promotion-section">
        <a
          :href="$router.resolve({ name: 'subscriptionAnnounce' }).href"
          class="premium-banner-link"
          @click="onClickPremiumBanner"
        >
          <img :src="bannerImageUrl" :alt="t('purchaseFish.premiumBannerAlt')" class="premium-banner-image" />
        </a>
      </div>

      <!-- ローディング状態 -->
      <LoadingSpinner v-if="isLoading" :text="t('purchaseFish.loading')" size="medium" variant="inline" />

      <!-- エラー状態 -->
      <div v-else-if="error" class="error-state">
        <p class="error-message">{{ error }}</p>
        <button class="retry-button" @click="fetchFishProducts">{{ t("purchaseFish.error.retry") }}</button>
      </div>

      <!-- 正常状態 -->
      <div v-else class="fish-list">
        <div class="fish-list-item" v-for="fish in fishList" :key="fish.id">
          <div class="fish-item-frame">
            <div v-if="calculateBonusFish(fish) > 0" class="fish-bonus-badge">
              {{ t("purchaseFish.product.advantageBadge", { amount: formatNumber(calculateBonusFish(fish)) }) }}
            </div>
            <div class="fish-item-content">
              <img class="fish-icon" src="@/assets/img/icon/fish_icon.png" :alt="t('purchaseFish.product.fishIcon')" />
              <div class="fish-amount">{{ formatNumber(fish.amount) }} {{ t("purchaseFish.product.fish") }}</div>
              <div v-if="fish.bonus > 0" class="fish-bonus-info">
                <img
                  class="premium-icon"
                  src="@/assets/img/icon/premium.png"
                  :alt="t('purchaseFish.product.premiumIcon')"
                />
                {{ t("purchaseFish.product.bonusWithAmount", { amount: formatNumber(fish.bonus) }) }}
              </div>
            </div>
          </div>
          <div class="fish-item-button-frame">
            <button class="fish-item-button" :disabled="isLoading || fishLoading" @click="onClickPurchase(fish)">
              ¥ {{ formatNumber(fish.price) }}
            </button>
          </div>
        </div>
      </div>
      <div class="fish-list-footer">
        <div>
          <ul>
            <li>{{ t("purchaseFish.notes.note1") }}</li>
            <li>{{ t("purchaseFish.notes.note2") }}</li>
            <li>{{ t("purchaseFish.notes.note3") }}</li>
            <li>{{ t("purchaseFish.notes.note4") }}</li>
          </ul>
        </div>
        <div class="commercial-transactions-act-link">
          <router-link to="/specifiedCommercialTransactionsAct" target="_blank">{{
            t("purchaseFish.commercialTransactionsAct")
          }}</router-link>
          <span class="link-separator">|</span>
          <router-link to="/paymentServicesAct" target="_blank">{{ t("purchaseFish.paymentServicesAct") }}</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import { Auth } from "aws-amplify";
import { NolaCoinClient } from "@/lib/clients/nolaCoinClient";
import { BillingClient } from "@/lib/clients/billingClient";
import { FishProduct } from "@/lib/models/nolaCoin";
import { CreateOneTimeCheckoutSessionInput } from "@/lib/models/billing";
import { showNotifyDialog } from "@/lib/dialog";
import { Plan } from "@/lib/models/user";
import ChevronLeftIcon from "icons/ChevronLeft.vue";
import PlusIcon from "icons/Plus.vue";
import LoadingSpinner from "@/components/atoms/LoadingSpinner.vue";
import { NolaAnalytics, NolaItemId } from "@/lib/utils/analytics";

// APIクライアントのインスタンス
const nolaCoinClient = new NolaCoinClient();
const billingClient = new BillingClient();

// Vue インスタンス
const instance = getCurrentInstance();
const store = instance?.proxy?.$store;

// アナリティクス
const nolaAnalytics = new NolaAnalytics(instance?.proxy.$gtm!, store!);

// i18n
const t = (key: string, params?: Record<string, unknown>): string => instance?.proxy?.$t(key, params) as string;

// リアクティブな状態管理
const fishList = ref<FishProduct[]>([]);
const isLoading = ref(false);
const error = ref<string | null>(null);
const initialFishLoading = ref(true); // 初期Fish読み込み状態

// 計算プロパティ
const formattedCurrentFish = computed(() => store?.getters["fishModule/formattedCurrentFish"]);
const formattedPrepaidFish = computed(() => store?.getters["fishModule/formattedPrepaidFish"]);
const formattedFreeFish = computed(() => store?.getters["fishModule/formattedFreeFish"]);
const storeFishLoading = computed(() => store?.getters["fishModule/isLoading"]);
const fishError = computed(() => store?.getters["fishModule/error"]);

// プレミアム関連
const plan = computed(() => store?.getters["billingModule/plan"]);
const isFreePlan = computed(() => plan.value === Plan.free);
const bannerImageUrl = computed(() =>
  // 画面幅576px以下をモバイルとして判定
  window.innerWidth <= 576 ? "/img/premium/recommend_sp.png" : "/img/premium/recommend_pc.png"
);

// Fish残高のローディング状態（初期表示 + store状態）
const fishLoading = computed(() => initialFishLoading.value || storeFishLoading.value);

const baseFishRate = computed(() => {
  if (!fishList.value || fishList.value.length === 0) {
    return 200; // デフォルト値(1円200Fish)
  }

  // 各商品のFish/価格の比率を計算し、最も低い比率を取得
  const rates = fishList.value.map((fish) => {
    const rate = fish.amount / fish.price;
    return rate;
  });

  const minRate = Math.min(...rates);

  return minRate;
});

const calculateBonusFish = (fish: FishProduct): number => {
  const realFish = fish.price * baseFishRate.value;
  return Math.round(fish.amount - realFish);
};

// メソッド
const formatNumber = (value: number): string => value.toLocaleString("ja-JP");

const fetchFishProducts = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    fishList.value = await nolaCoinClient.getFishProducts();
  } catch (err) {
    console.error("Fish商品の取得に失敗しました:", err);
    error.value = t("purchaseFish.error.loadFailed");
    fishList.value = []; // エラー時は空配列にして、エラー表示を優先
  } finally {
    isLoading.value = false;
  }
};

const retryFetchFishData = async () => {
  try {
    await store?.dispatch("fishModule/fetchFishData");
  } finally {
    initialFishLoading.value = false;
  }
};

const PREPAID_WEB_LIMIT = 100000000; // 1億fish

const prepaidFish = computed(() => (store?.getters["fishModule/prepaidFish"] as number) ?? 0);

const onClickPurchase = async (fishProduct: FishProduct) => {
  // アナリティクス: Fish商品購入ボタンクリックイベントの発火
  nolaAnalytics.logButtonEvent(`${NolaItemId.PurchaseFishPackage}_${fishProduct.price}`);

  // prepaid_webの上限チェック（1億fish）
  if (prepaidFish.value + fishProduct.amount > PREPAID_WEB_LIMIT) {
    await showNotifyDialog({
      title: t("purchaseFish.error.exceedsLimitTitle"),
      content: t("purchaseFish.error.exceedsLimit"),
    });
    return;
  }

  try {
    const user = await Auth.currentUserPoolUser().catch(() => null);
    if (!user) {
      console.error(t("purchaseFish.error.authRequired"));
      return;
    }

    const { email } = user.attributes;
    const payload: CreateOneTimeCheckoutSessionInput = {
      email,
      itemId: fishProduct.id,
      successUrl: `${window.location.origin}/mypage`,
      cancelUrl: `${window.location.origin}/purchase-fish`,
    };

    const session = await billingClient.createOneTimeCheckoutSession(payload);
    window.location.href = session.url;
  } catch (err) {
    console.error("購入画面への遷移に失敗しました:", err);
    await showNotifyDialog({
      title: t("purchaseFish.error.purchaseFailedTitle"),
      content: t("purchaseFish.error.purchaseFailed"),
    });
  }
};

const onClickPremiumBanner = () => {
  // アナリティクス: プレミアム誘導バナークリックイベントの発火
  nolaAnalytics.logButtonEvent(NolaItemId.PurchaseFishPremiumBanner);
};

// ライフサイクルフック
onMounted(async () => {
  // Fish商品データを取得
  await fetchFishProducts();
  // MyPageと同様にFish残高データを取得
  try {
    await store?.dispatch("fishModule/fetchFishData");
  } finally {
    initialFishLoading.value = false;
  }
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.purchase-fish-header {
  display: flex;
  flex-direction: column;
  padding: 20px 0;

  .back-link {
    align-self: flex-start;
    color: var(--color-primary);
    text-decoration: none;
    font-size: var(--font-size-sm);
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 4px;
    line-height: 1;

    &:hover {
      text-decoration: underline;
    }
  }

  .purchase-fish-header-title {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-bold);
    text-align: left;
    color: var(--color-text-normal);
  }
}

.purchase-fish-content {
  max-width: 750px;
  padding: 20px;
  margin: auto;
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);
}

.fish-info {
  background-color: var(--color-surface-base);
  padding: 20px;
  border-radius: var(--radius-lg);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  position: relative;
  color: var(--color-text-normal);

  .fish-info-title {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-low);
  }

  .fish-info-data {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;

    .fish-info-data-title {
      font-size: var(--font-size-xxl);
      font-weight: var(--font-weight-bold);
      line-height: 2;
      color: var(--color-text-normal);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;

      .fish-info-data-title-icon {
        width: 34px;
        height: 34px;
      }
    }

    .fish-info-data-description {
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: var(--font-weight-bold);

      .fish-info-data-description-item {
        min-width: 6.25rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        .fish-category {
          font-size: var(--font-size-xs);
          color: var(--color-text-low);
          font-weight: var(--font-weight-regular);
        }

        .fish-value {
          font-size: var(--font-size-sm);
          color: var(--color-text-normal);
        }
      }

      .fish-info-data-description-item-plus {
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-bold);
        color: var(--color-text-low);
        display: flex;
        align-items: center;
        justify-content: center;

        :deep(.material-design-icon) {
          width: 14px;
          height: 14px;
        }

        :deep(.material-design-icon__svg) {
          width: 14px;
          height: 14px;
          stroke-width: 2px;
          stroke: currentColor;
          fill: none;
        }
      }
    }
  }
}

.fish-list {
  margin: 40px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;

  .fish-list-item {
    flex: 0 1 calc(33.333% - 15px);
    min-width: 220px;
    max-width: 280px;
  }

  .fish-item-frame {
    background-color: var(--color-surface-base);
    border-radius: var(--radius-lg);
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
    position: relative;
    color: var(--color-text-normal);

    .fish-bonus-badge {
      position: absolute;
      top: 10px;
      right: 10px;
      background-color: var(--color-warning);
      color: var(--color-text-on-fixed-bg);
      font-size: var(--font-size-xs);
      font-weight: var(--font-weight-bold);
      padding: 5px 10px 3px 10px;
      border-radius: var(--radius-md);
      white-space: nowrap;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    }

    .fish-item-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      flex: 1;
      padding-top: 16px;
      gap: 10px;

      .fish-icon {
        width: 60px;
        height: 60px;
        box-sizing: border-box;
        img {
          width: 100%;
          height: 100%;
        }
      }

      .fish-amount {
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-bold);
        color: var(--color-text-normal);
        text-align: center;
      }

      .fish-bonus-info {
        font-size: var(--font-size-xs);
        color: var(--color-primary);
        font-weight: var(--font-weight-bold);
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        line-height: 1;

        .premium-icon {
          width: 16px;
          height: 16px;
          display: block;
          flex-shrink: 0;
        }
      }
    }
  }

  .fish-item-button-frame {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 20px;

    .fish-item-button {
      position: relative;
      background-color: var(--color-primary);
      color: var(--color-text-on-fixed-bg);
      width: 160px;
      height: 44px;
      font-size: var(--font-size-lg);
      font-weight: var(--font-weight-bold);
      border: none;
      border-radius: var(--radius-sm);
      cursor: pointer;

      @include hover-overlay;

      &:disabled {
        background-color: var(--color-surface-muted);
        color: var(--color-inactive);
        border: solid 1px var(--color-border);
        cursor: not-allowed;
        &::after {
          display: none;
        }
      }
    }
  }
}

.fish-list-footer {
  background-color: var(--color-surface-base);
  padding: 20px 12px;
  margin: 16px 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  color: var(--color-text-normal);

  .commercial-transactions-act-link {
    margin: auto;
    a {
      color: var(--color-primary);
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-bold);
      &:hover {
        text-decoration: none;
      }
    }
    .link-separator {
      margin: 0 0.5rem;
      color: var(--color-text-low);
    }
  }
}

.error-state,
.fish-error {
  text-align: center;
  padding: 40px 20px;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.error-state {
  .error-message {
    color: var(--color-warning);
    margin-bottom: 20px;
    font-size: var(--font-size-md);
  }

  .retry-button {
    position: relative;
    background-color: var(--color-primary);
    color: var(--color-text-on-fixed-bg);
    border: none;
    padding: 10px 20px;
    border-radius: var(--radius-xs);
    cursor: pointer;
    font-size: var(--font-size-sm);

    @include hover-overlay;
  }
}

// プレミアム誘導バナーセクション
.premium-promotion-section {
  margin: 20px 0;
  padding: 0;

  @media (max-width: 576px) {
    margin: 16px 0;
  }
}

.premium-banner-link {
  display: block;
  width: 100%;
  transition: opacity 0.2s ease, transform 0.2s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}

.premium-banner-image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-sm);
}

// スケルトンローダー用CSS（マイページと同じ）
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.skeleton-text {
  background-color: var(--color-surface-muted);
  border-radius: var(--radius-xs);
  position: relative;
  overflow: hidden;
  display: inline-block;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent);
    animation: shimmer 1.5s infinite;
  }

  &.skeleton-amount {
    width: 120px;
    height: 48px;
    margin: 8px 0;
    vertical-align: middle;
  }

  &.skeleton-value {
    width: 60px;
    height: 18px;
    vertical-align: middle;
  }
}
</style>
